import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import { useEffect, useState } from 'react';
import { default as Image } from 'antd/es/image';
import { default as Skeleton } from 'antd/es/skeleton';
import rehypeRaw from 'rehype-raw';

import { loadMd } from '../utils/loadMd';
import { THEMES } from '../hooks/useDarkMode';
import { useTheme } from '../hooks/useTheme';
import { basePath } from '../basePath';
import { InpayPublicSSLCertificateLink } from './InpayCertificateLink';
import { InpayPrivateAndCertificatePairKeyModal } from './InpayPrivateAndCertificatePairKeyModal';
import { enhanceLinkWithToken } from '../utils/enhanceLinkWithToken';

export type Resource = {
  filePath: string;
};

export function Resource({
  resource,
  onLoad
}: {
  resource: Resource;
  onLoad?: () => void;
}) {
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(false);

  const { theme } = useTheme();

  useEffect(() => {
    setLoading(true);
    loadMd(resource.filePath.split('.')[0]).then((text) => {
      if (text) {
        setMarkdownContent(text);
      }
      setLoading(false);
      onLoad?.();
    });
  }, [onLoad, resource]);

  if (loading) {
    return <Skeleton active />;
  }

  if (!resource) {
    return null;
  }

  return (
    <Markdown
      className="markdown"
      // the rehypeRaw plugin makes it so that if you have html inside your .md file, it will be treated as html
      rehypePlugins={[rehypeRaw]}
      components={{
        a(props) {
          const { node, href, id, ...rest } = props;

          if (id === 'customer-app-link') {
            return <Link to={window.location.origin} {...rest} />;
          }

          if (id === 'inpay-public-ssl-certificate') {
            return <InpayPublicSSLCertificateLink />;
          }

          if (id === 'inpay-private-and-certificate-key-pair') {
            return <InpayPrivateAndCertificatePairKeyModal />;
          }

          if (!href) {
            return <a {...rest} />;
          }

          return (
            <Link target="_blank" to={enhanceLinkWithToken(href)} {...rest} />
          );
        },
        img(props) {
          const { src } = props;

          const newSrc = `/${basePath}/${src}`;

          const darkVariant = newSrc?.replace('light', 'dark');

          // If the dark variant is not found, the light variant (or original src) will be used
          const imageVariant = theme === THEMES.light ? newSrc : darkVariant;

          return (
            <div className="image-wrapper">
              <Image
                preview={{
                  src: darkVariant,
                  style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)'
                  }
                }}
                src={imageVariant}
                fallback={newSrc}
                loading="lazy"
              />
            </div>
          );
        },
        code(props) {
          const { children, className, ...rest } = props;
          const match = /language-(\w+)/.exec(className || '');

          if (!match) return <code>{children}</code>;

          return (
            <pre className="exception language-none overflow-auto rounded-md bg-inpay-dark-green-1000 p-2 text-white">
              <code className="exception whitespace-pre-line" {...rest}>
                {children}
              </code>
            </pre>
          );
        }
      }}
    >
      {markdownContent}
    </Markdown>
  );
}
